import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ShieldCheck, Clock3, MapPinned, Activity, MessageSquareHeart, Scale, Crown } from "lucide-react";
import { MaskedLines, Appear } from "@/components/Reveal";
import { StoreBadges } from "@/components/StoreBadges";
import { PhoneMock } from "@/components/PhoneMock";

const WAITS = [
  { label: "ER · 24m", cls: "border-[#2ee27a]/50 bg-[#2ee27a]/10 text-[#2ee27a]" },
  { label: "38m", cls: "border-[#ffc000]/50 bg-[#ffc000]/10 text-[#ffc000]" },
  { label: "1h 12m", cls: "border-[#ff3b30]/50 bg-[#ff3b30]/10 text-[#ff6a5e]" },
];

const FEATURES = [
  { icon: <Activity size={14} />, label: "Live ER waits" },
  { icon: <MessageSquareHeart size={14} />, label: "AI triage" },
  { icon: <Scale size={14} />, label: "Compare hospitals" },
];

const PILLS = [
  { icon: <Clock3 size={14} />, label: "Live wait estimates" },
  { icon: <MapPinned size={14} />, label: "ERs near you" },
  { icon: <ShieldCheck size={14} />, label: "GPS never stored on our servers" },
];

const TiltPhone = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const py = useTransform(scrollY, [0, 700], [0, 90]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div style={{ y: py }} className="relative isolate flex justify-center lg:justify-end" ref={ref} onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        initial={{ opacity: 0, y: 60, rotate: -4 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
        style={{ perspective: 1200 }}
      >
        <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
          <PhoneMock />
        </motion.div>
      </motion.div>
      <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[560px] w-[560px] rounded-full bg-[#1f8fff]/40 blur-[110px]" aria-hidden="true" />
      <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[300px] rounded-full bg-[#4daaff]/35 blur-[70px]" aria-hidden="true" />
    </motion.div>
  );
};

export const Hero = () => (
  <section className="relative overflow-hidden vignette">
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-32 pb-20 sm:pt-40 sm:pb-28 lg:pt-44 lg:pb-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <Appear delay={0.1}>
            <p data-testid="hero-eyebrow" className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--clinical)] flex items-center gap-3">
              <span className="h-px w-8 bg-[var(--clinical)]" /> Live ER &amp; clinic wait times across Canada
            </p>
          </Appear>
          <MaskedLines
            as="h1"
            data-testid="hero-main-title"
            lines={["Find care faster."]}
            delay={0.3}
            className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-[4.6rem] font-extrabold tracking-[-0.03em] leading-[1.02] text-white"
          />
          <Appear delay={0.85}>
            <p data-testid="hero-subtitle" className="mt-6 max-w-lg text-base sm:text-lg text-[#b7c2d4] leading-relaxed">
              Live ER &amp; clinic wait times, AI triage and verified doctor reviews — near you.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2.5" data-testid="hero-wait-chips" aria-label="Example wait times">
              {WAITS.map((w) => (
                <li key={w.label} className={`inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-bold tracking-tight ${w.cls}`}>
                  {w.label}
                </li>
              ))}
            </ul>
            <ul className="mt-4 flex flex-wrap gap-2.5" data-testid="hero-feature-pills">
              {FEATURES.map((f) => (
                <li key={f.label} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-[#e3e8f0]">
                  <span className="text-[var(--clinical)]">{f.icon}</span>
                  {f.label}
                </li>
              ))}
            </ul>
          </Appear>
          <Appear delay={1}>
            <div className="mt-8">
              <StoreBadges />
            </div>
            <a
              href="#founding-member"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector("[data-testid='gallery-shot-5']");
                if (!el) return;
                el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
              }}
              data-testid="founding-member-callout"
              className="group mt-5 inline-flex items-center gap-3 rounded-2xl sm:rounded-full border border-[#f59e0b]/35 bg-[#f59e0b]/10 pl-2 pr-4 py-1.5 text-sm text-[#e3e8f0] transition-colors hover:border-[#f59e0b]/60 hover:bg-[#f59e0b]/15"
            >
              <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#f59e0b] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#1b2534]">
                <Crown size={11} strokeWidth={2.5} /> Founding member
              </span>
              <span>
                First 500 members get <span className="font-semibold text-white">20% off yearly — for life</span>
              </span>
            </a>
          </Appear>
          <Appear delay={1.15}>
            <ul className="mt-8 flex flex-wrap gap-2.5" data-testid="hero-trust-pills">
              {PILLS.map((p) => (
                <li key={p.label} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur px-3.5 py-1.5 text-xs font-medium text-slate-200">
                  <span className="text-[var(--clinical)]">{p.icon}</span>
                  {p.label}
                </li>
              ))}
            </ul>
          </Appear>
        </div>
        <div className="lg:col-span-5">
          <TiltPhone />
        </div>
      </div>
    </div>
  </section>
);
